import React, { useState, useEffect, useRef } from "react";
import { Button } from "../ui";
import NativeInput from "../NativeInput";
import CustomDropdown from "../dropdown/CustomDropdown";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useTranslation } from "react-i18next";
import { api } from "../../services";
import { translateProductApiMessage } from "../../utils/translateProductApiMessage";

const CatalogModal = ({
  show,
  setShow,
  onCatalogUpdate,
  isEditMode = false,
  editData = null,
}) => {
  const { t, i18n } = useTranslation("translation");
  const isArabic = i18n.language === "ar";
  const modalRef = useRef(null);
  const cancelButtonRef = useRef(null);

  const [name_en, setName_en] = useState("");
  const [name_ar, setName_ar] = useState("");
  const [description_en, setDescription_en] = useState("");
  const [description_ar, setDescription_ar] = useState("");
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState({});

  const resetForm = () => {
    setError({});
    setSelectedStore(null);
    setName_en("");
    setName_ar("");
    setDescription_en("");
    setDescription_ar("");
  };

  const toggleShow = () => {
    resetForm();
    setShow(false);
  };

  useEffect(() => {
    if (!show) {
      if (!isEditMode) resetForm();
      return;
    }

    setError({});

    if (isEditMode && editData) {
      setName_en(editData.name_en || editData.name || "");
      setName_ar(editData.name_ar || editData.name || "");
      setDescription_en(
        editData.description_en || editData.description || ""
      );
      setDescription_ar(
        editData.description_ar || editData.description || ""
      );
    } else if (!isEditMode) {
      resetForm();
    }
  }, [show, isEditMode, editData]);

  useEffect(() => {
    const getMyStores = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await api.get("/stores/my", {
          headers: { Authorization: `Bearer ${token}` },
        });
        setStores(response.data.stores || []);
      } catch (err) {
        console.error("Error fetching stores:", err);
      }
    };
    getMyStores();
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && !loading) {
        toggleShow();
      }
    };

    if (show) {
      document.addEventListener("keydown", handleKeyDown);
      if (cancelButtonRef.current) {
        cancelButtonRef.current.focus();
      }
      return () => document.removeEventListener("keydown", handleKeyDown);
    }
  }, [show, loading]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !loading) {
      toggleShow();
    }
  };

  const handleCreateCatalog = async (payload) => {
    const token = localStorage.getItem("token");
    const response = await api.post(
      "/v2.1/catalog",
      {
        ...payload,
        name_en: payload.name_en,
        name_ar: payload.name_ar,
        description_en: payload.description_en || "",
        description_ar: payload.description_ar || "",
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );
    toast.success(t("Catalog Added Successfully"));
    if (onCatalogUpdate) onCatalogUpdate();
    return response;
  };

  const handleUpdateCatalog = async (id, payload) => {
    const token = localStorage.getItem("token");
    const updatePayload = {
      name_en: payload.name_en,
      name_ar: payload.name_ar,
      description_en: payload.description_en || "",
      description_ar: payload.description_ar || "",
    };

    const response = await api.put(`/v2.1/catalog/${id}`, updatePayload, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    const successMessage = translateProductApiMessage(
      response.data?.message,
      t
    ) || t("productApiMsgs.catalogUpdated");
    toast.success(successMessage);
    if (onCatalogUpdate) onCatalogUpdate();
    return response;
  };

  const handleSubmit = async () => {
    const errors = {};

    if (!name_en.trim()) {
      errors.nameEn = t("English name is required.");
    } else if (/[\u0600-\u06FF]/.test(name_en)) {
      errors.nameEn = t(
        "English name must only contain English letters and numbers."
      );
    }

    if (!name_ar.trim()) {
      errors.nameAr = t("Arabic name is required.");
    } else if (/[A-Za-z]/.test(name_ar)) {
      errors.nameAr = t(
        "Arabic name must only contain Arabic letters and numbers."
      );
    }

    if (!isEditMode && !selectedStore) {
      errors.store = t("Store is required");
    }

    if (description_en && /[\u0600-\u06FF]/.test(description_en)) {
      errors.descriptionEn = t(
        "English description must only contain English letters and numbers."
      );
    }

    if (description_ar && /[A-Za-z]/.test(description_ar)) {
      errors.descriptionAr = t(
        "Arabic description must only contain Arabic letters and numbers."
      );
    }

    if (Object.keys(errors).length > 0) {
      setError(errors);
      const firstError = Object.keys(errors)[0];
      const errorElement = document.querySelector(
        `[data-error-field="${firstError}"]`
      );
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return;
    }

    setLoading(true);
    try {
      if (isEditMode) {
        const catalogId = editData?.id || editData?._id;
        const updatePayload = {
          name_en: name_en.trim(),
          name_ar: name_ar.trim(),
          description_en: description_en.trim(),
          description_ar: description_ar.trim(),
        };
        const response = await handleUpdateCatalog(catalogId, updatePayload);
        if (response.status === 200 || response.status === 201) {
          toggleShow();
        }
      } else {
        const createData = {
          store_id: selectedStore._id,
          name_en,
          name_ar,
          description_en,
          description_ar,
          date: new Date().toISOString(),
        };
        const response = await handleCreateCatalog(createData);
        if (response.status === 200 || response.status === 201) {
          toggleShow();
        }
      }
    } catch (e) {
      console.error(
        `Error ${isEditMode ? t("updating") : t("creating")} catalog:`,
        e
      );

      if (!e.response?.data?.errors) {
        const errorMessage =
          e.response?.data?.error ||
          `${t("Failed to")} ${isEditMode ? t("update") : t("create")} ${t("catalog. Please try again.")}`;
        toast.error(errorMessage, { autoClose: 5000 });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleStoreSelect = (store) => {
    const isUnselecting = !store || (store && (!store._id || store._id === ""));
    setSelectedStore(isUnselecting ? null : store);

    if (isUnselecting) {
      setError((prev) => ({ ...prev, store: t("Store is required") }));
    } else {
      setError((prev) => ({ ...prev, store: undefined }));
    }
  };

  if (!show) return null;

  const submitLabel = loading
    ? isEditMode
      ? t("updating")
      : t("creating")
    : isEditMode
      ? t("update")
      : t("create");

  const submitButton = (
    <Button
      type="button"
      color="primary"
      size="md"
      onClick={handleSubmit}
      disabled={loading}
      className={isArabic ? "me-2" : ""}
    >
      {loading ? (
        <>
          <span
            className="spinner-border spinner-border-sm me-2"
            role="status"
            aria-hidden="true"
          />
          {submitLabel}
        </>
      ) : (
        submitLabel
      )}
    </Button>
  );

  const cancelButton = (
    <Button
      ref={cancelButtonRef}
      type="button"
      color="secondary"
      size="md"
      onClick={toggleShow}
      disabled={loading}
      className={isArabic ? "" : "me-2"}
    >
      {t("cancel")}
    </Button>
  );

  return (
    <div
      className="modal fade show d-block oh-modal"
      style={{
        backgroundColor: "rgba(0,0,0,0.5)",
        backdropFilter: "blur(4px)",
        WebkitBackdropFilter: "blur(4px)",
      }}
      onClick={handleBackdropClick}
    >
      <div className="modal-dialog modal-dialog-centered" ref={modalRef}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              {isEditMode ? t("editcatalog") : t("createcatalog")}
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={toggleShow}
              disabled={loading}
              aria-label="Close"
            />
          </div>

          <div className="modal-body">
            <div className="row g-3">
              {!isEditMode && (
                <div className="col-12" data-error-field="store">
                  <CustomDropdown
                    options={stores}
                    handleSelect={handleStoreSelect}
                    selectedOption={selectedStore}
                    placeholder={t("Select store")}
                    backgroundColor="#f9f9f9"
                    label={t("store")}
                    invalid={!!error.store}
                  />
                  {error.store && (
                    <div className="text-danger oh-field-error">{error.store}</div>
                  )}
                </div>
              )}

              <div className="col-12" data-error-field="nameEn">
                <NativeInput
                  label={
                    <span>
                      {t("nameen")} <span className="text-danger">*</span>
                    </span>
                  }
                  value={name_en}
                  invalid={!!error.nameEn}
                  feedback={error.nameEn || ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    setName_en(value);
                    const hasArabic = /[\u0600-\u06FF]/.test(value);
                    setError((prev) => {
                      if (!value.trim()) {
                        return {
                          ...prev,
                          nameEn: t("English name is required."),
                        };
                      }
                      if (hasArabic) {
                        return {
                          ...prev,
                          nameEn: t(
                            "English name must only contain English letters and numbers."
                          ),
                        };
                      }
                      const { nameEn, ...rest } = prev;
                      return rest;
                    });
                  }}
                  required
                />
              </div>

              <div className="col-12" data-error-field="nameAr">
                <NativeInput
                  label={
                    <span>
                      {t("namear")} <span className="text-danger">*</span>
                    </span>
                  }
                  value={name_ar}
                  invalid={!!error.nameAr}
                  feedback={error.nameAr || ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    setName_ar(value);
                    setError((prev) => {
                      if (!value.trim()) {
                        return {
                          ...prev,
                          nameAr: t("Arabic name is required."),
                        };
                      }
                      if (/[A-Za-z]/.test(value)) {
                        return {
                          ...prev,
                          nameAr: t(
                            "Arabic name must only contain Arabic letters and numbers."
                          ),
                        };
                      }
                      const { nameAr, ...rest } = prev;
                      return rest;
                    });
                  }}
                  required
                />
              </div>

              <div className="col-12" data-error-field="descriptionEn">
                <NativeInput
                  label={t("descriptionen")}
                  value={description_en}
                  invalid={!!error.descriptionEn}
                  feedback={error.descriptionEn || ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    setDescription_en(value);
                    const hasArabic = /[\u0600-\u06FF]/.test(value);
                    setError((prev) => {
                      if (value && hasArabic) {
                        return {
                          ...prev,
                          descriptionEn: t(
                            "English description must only contain English letters and numbers."
                          ),
                        };
                      }
                      const { descriptionEn, ...rest } = prev;
                      return rest;
                    });
                  }}
                />
              </div>

              <div className="col-12" data-error-field="descriptionAr">
                <NativeInput
                  label={t("descriptionar")}
                  value={description_ar}
                  invalid={!!error.descriptionAr}
                  feedback={error.descriptionAr || ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    setDescription_ar(value);
                    const hasLatin = /[A-Za-z]/.test(value);
                    setError((prev) => {
                      if (value && hasLatin) {
                        return {
                          ...prev,
                          descriptionAr: t(
                            "Arabic description must only contain Arabic letters and numbers."
                          ),
                        };
                      }
                      const { descriptionAr, ...rest } = prev;
                      return rest;
                    });
                  }}
                />
              </div>
            </div>
          </div>

          <div className="modal-footer justify-content-end">
            {isArabic ? (
              <>
                {submitButton}
                {cancelButton}
              </>
            ) : (
              <>
                {cancelButton}
                {submitButton}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CatalogModal;
